import React, { useEffect, useRef, useState } from "react";

import styled from "styled-components";

import Icon from "components/atoms/Icon";
import { MessageContent } from "components/atoms/message";
import {
    formatReviewThreadCreatedAt,
    formatReviewThreadLocation,
    getReviewThreadExtraMessageCount,
    getReviewThreadFirstMessage,
    getReviewThreadMessageCountLabel,
    getReviewThreadSelectedText,
    getReviewThreadStatusLabel,
} from "components/organisms/manuscript/review/formatters";

const PanelHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 12px 14px;
    border-bottom: 1px solid var(--color-Line);
    background: var(--color-Base1);
`;

const HeaderInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
`;

const HeaderText = styled.div`
    min-width: 0;
`;

const Title = styled.div`
    font-size: 13px;
    font-weight: 800;
    color: var(--color-NormalBlack);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Subtitle = styled.div`
    font-size: 11px;
    color: var(--color-Grey1);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const RailBody = styled.div`
    flex: 1;
    min-height: 0;
    padding: 10px;
    overflow: auto;
    background: var(--color-White);
`;

const Stack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const MetaValue = styled.div`
    min-width: 0;
    color: var(--color-SubBlack);
    word-break: break-word;
`;

const ReviewList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const ReviewToolbar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    flex-wrap: wrap;
`;

const ReviewFilterGroup = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 4px;
`;

const CompactTextButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 24px;
    padding: 0 8px;
    border: 1px solid var(--color-Line);
    border-radius: 6px;
    background: var(--color-White);
    color: var(--color-SubBlack);
    font-size: 10px;
    font-weight: 700;
    cursor: pointer;

    &:hover {
        background: var(--color-ButtonHover4);
    }
`;

const ReviewThreadItem = styled.button`
    display: flex;
    flex-direction: column;
    gap: 5px;
    width: 100%;
    padding: 8px 10px;
    border: 1px solid ${(props) => (props.$active ? "var(--color-Key)" : "var(--color-Line)")};
    border-radius: 9px;
    background: ${(props) => (props.$active ? "var(--color-ButtonHover4)" : "var(--color-White)")};
    text-align: left;
    cursor: pointer;

    &:hover {
        background: var(--color-ButtonHover4);
    }
`;

const ReviewThreadTop = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;

const ReviewThreadTitle = styled.div`
    font-size: 11px;
    font-weight: 700;
    color: var(--color-NormalBlack);
`;

const ReviewThreadTitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    flex-wrap: wrap;
`;

const ReviewThreadCount = styled.span`
    font-size: 10px;
    font-weight: 700;
    color: var(--color-Grey1);
`;

const ReviewBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    height: 18px;
    padding: 0 7px;
    border-radius: 999px;
    background: ${(props) => (props.$resolved ? "var(--color-ButtonHover4)" : "var(--color-ButtonHover4)")};
    color: ${(props) => (props.$resolved ? "var(--color-SubBlack)" : "var(--color-Key)")};
    font-size: 9px;
    font-weight: 800;
`;

const ReviewThreadLine = styled.div`
    font-size: 10px;
    color: var(--color-Grey1);
`;

const ReviewThreadSelectedText = styled.div`
    font-size: 11px;
    color: var(--color-SubBlack);
    display: -webkit-box;
    overflow: hidden;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
`;

const ReviewThreadMessageCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 9px 10px;
    border: 1px solid var(--color-Line);
    border-radius: 10px;
    background: var(--color-White);
`;

const ReviewThreadMessageMeta = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    font-size: 10px;
    color: var(--color-Grey1);
`;

const ReviewThreadPreview = styled.div`
    font-size: 11px;
    line-height: 1.4;
    color: var(--color-SubBlack);
    display: -webkit-box;
    overflow: hidden;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
`;

const ReviewThreadExtraMessages = styled.div`
    font-size: 10px;
    color: var(--color-Grey1);
`;

function ManuscriptReviewPanel({ reviewThreads, selectedReviewThreadId, onSelectReviewThread, onReviewThreadMount }) {
    const [reviewFilter, setReviewFilter] = useState("open");
    const selectedReviewThreadRef = useRef(null);
    const getAuthorLabel = (name, email) => name || email || "알 수 없음";
    const visibleReviewThreads = reviewThreads.filter((thread) => {
        if (reviewFilter === "all") return true;
        if (reviewFilter === "resolved") return thread.is_resolved;
        return !thread.is_resolved;
    });
    const openReviewCount = reviewThreads.filter((thread) => !thread.is_resolved).length;
    const resolvedReviewCount = reviewThreads.filter((thread) => thread.is_resolved).length;
    const totalReviewCount = reviewThreads.length;

    useEffect(() => {
        if (!selectedReviewThreadRef.current) return;
        selectedReviewThreadRef.current.scrollIntoView({
            block: "nearest",
            behavior: "smooth",
        });
    }, [selectedReviewThreadId, reviewFilter]);

    useEffect(() => {
        const selectedThread = reviewThreads.find((thread) => String(thread.id) === String(selectedReviewThreadId));
        if (selectedThread?.is_resolved) setReviewFilter("resolved");
    }, [reviewThreads, selectedReviewThreadId]);

    return (
        <>
            <PanelHeader>
                <HeaderInfo>
                    <Icon name={"thread"} size={"16px"} color={"var(--color-Key)"} />
                    <HeaderText>
                        <Title>리뷰</Title>
                        <Subtitle>선택 영역에 코멘트를 남깁니다.</Subtitle>
                    </HeaderText>
                </HeaderInfo>
            </PanelHeader>
            <RailBody>
                <Stack>
                    <ReviewToolbar>
                        <ReviewFilterGroup>
                            <CompactTextButton
                                type="button"
                                onClick={() => setReviewFilter("open")}
                                style={
                                    reviewFilter === "open"
                                        ? { borderColor: "var(--color-Key)", color: "var(--color-Key)" }
                                        : undefined
                                }
                            >
                                열림 ({openReviewCount})
                            </CompactTextButton>
                            <CompactTextButton
                                type="button"
                                onClick={() => setReviewFilter("resolved")}
                                style={
                                    reviewFilter === "resolved"
                                        ? { borderColor: "var(--color-Key)", color: "var(--color-Key)" }
                                        : undefined
                                }
                            >
                                해결됨 ({resolvedReviewCount})
                            </CompactTextButton>
                            <CompactTextButton
                                type="button"
                                onClick={() => setReviewFilter("all")}
                                style={
                                    reviewFilter === "all"
                                        ? { borderColor: "var(--color-Key)", color: "var(--color-Key)" }
                                        : undefined
                                }
                            >
                                전체 ({totalReviewCount})
                            </CompactTextButton>
                        </ReviewFilterGroup>
                    </ReviewToolbar>

                    <ReviewList>
                        {visibleReviewThreads.length ? (
                            visibleReviewThreads.map((thread) => {
                                const firstMessage = getReviewThreadFirstMessage(thread);
                                const location = formatReviewThreadLocation(thread.creation_anchor_range);
                                const selectedText = getReviewThreadSelectedText(thread);
                                const extraMessageCount = getReviewThreadExtraMessageCount(thread);
                                return (
                                    <ReviewThreadItem
                                        key={thread.id}
                                        type="button"
                                        data-testid={`review-thread-item-${thread.id}`}
                                        ref={(element) => {
                                            if (thread.id === selectedReviewThreadId) {
                                                selectedReviewThreadRef.current = element;
                                            }
                                            onReviewThreadMount?.(thread.id, element);
                                        }}
                                        $active={thread.id === selectedReviewThreadId}
                                        onClick={() => onSelectReviewThread?.(thread.id, { source: "panel" })}
                                    >
                                        <ReviewThreadTop>
                                            <ReviewThreadTitleRow>
                                                <ReviewThreadTitle>{thread.anchor_path}</ReviewThreadTitle>
                                                <ReviewThreadCount>
                                                    {getReviewThreadMessageCountLabel(thread)}
                                                </ReviewThreadCount>
                                            </ReviewThreadTitleRow>
                                            <ReviewBadge $resolved={thread.is_resolved}>
                                                {getReviewThreadStatusLabel(thread)}
                                            </ReviewBadge>
                                        </ReviewThreadTop>
                                        {location ? <ReviewThreadLine>{location}</ReviewThreadLine> : null}
                                        {selectedText ? (
                                            <ReviewThreadSelectedText>{selectedText}</ReviewThreadSelectedText>
                                        ) : null}
                                        <ReviewThreadMessageCard>
                                            <ReviewThreadMessageMeta>
                                                <span>
                                                    {getAuthorLabel(
                                                        firstMessage?.sender_name || thread.created_by_name,
                                                        firstMessage?.sender_email || thread.created_by_email,
                                                    )}
                                                </span>
                                                <span>
                                                    {formatReviewThreadCreatedAt(
                                                        firstMessage?.created_at || thread.created_at,
                                                    )}
                                                </span>
                                            </ReviewThreadMessageMeta>
                                            <ReviewThreadPreview>
                                                {firstMessage?.content ? (
                                                    <MessageContent content={firstMessage.content} />
                                                ) : (
                                                    "첫 메시지가 없습니다."
                                                )}
                                            </ReviewThreadPreview>
                                        </ReviewThreadMessageCard>
                                        {extraMessageCount ? (
                                            <ReviewThreadExtraMessages>
                                                추가 메시지 {extraMessageCount}개
                                            </ReviewThreadExtraMessages>
                                        ) : null}
                                    </ReviewThreadItem>
                                );
                            })
                        ) : (
                            <MetaValue>표시할 리뷰가 없습니다.</MetaValue>
                        )}
                    </ReviewList>
                </Stack>
            </RailBody>
        </>
    );
}

export default ManuscriptReviewPanel;
